/**
 * Type definitions for reading notes (thoughtpad feature)
 */

export type NoteType = 'chat_answer' | 'highlight' | 'thought';

export interface ReadingNoteBase {
  page_number: number;
  note_type: NoteType;
  content_text?: string;
  image_urls?: string[];
  user_note?: string;
  concept_tags?: string[];
  note_metadata?: Record<string, any>;
  chat_message_id?: string;  // For chat_answer notes: ID of AI response message
  source_question_id?: string;  // For chat_answer notes: ID of user question message
}

export interface ReadingNoteCreate extends ReadingNoteBase {
  book_id: string;
}

export type IndexingStatus = 'pending' | 'running' | 'completed' | 'failed' | 'no_task';

export interface ReadingNote extends ReadingNoteBase {
  id: string;
  user_id: string;
  book_id: string;
  created_at: Date;
  updated_at: Date;

  // Chapter information (computed by backend)
  chapter_number?: number;
  chapter_name?: string;

  // Indexing status fields
  is_indexed?: boolean;
  indexed_at?: Date;
  indexing_status?: IndexingStatus;
  indexing_error?: string;

  // Indexing metadata (from backend)
  normalized_content?: string;
  extracted_concept_tags?: string[];
  attachment_context?: string;
}

export interface MultiPageSelection {
  pageNumber: number;
  text: string;
}

export interface SelectionBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface NoteMetadata {
  selection_bounds?: SelectionBounds;
  zoom_level?: number;
  multi_page_selection?: boolean;
  selection_group_id?: string;
  ai_model?: string;
  chat_session_id?: string;
  attachment_type?: 'snippet' | 'upload';
}

// Helper function to create notes from multi-page selections
export function createNotesFromMultiPageSelection(
  bookId: string,
  selections: MultiPageSelection[],
  userNote: string,
  conceptTags: string[],
  selectionGroupId: string
): ReadingNoteCreate[] {
  return selections.map(selection => ({
    book_id: bookId,
    page_number: selection.pageNumber,
    note_type: 'highlight' as const,
    content_text: `[Page ${selection.pageNumber}]\n${selection.text}`,
    user_note: userNote,
    concept_tags: conceptTags,
    note_metadata: {
      multi_page_selection: true,
      selection_group_id: selectionGroupId
    }
  }));
}